import React, { useEffect, useRef, useState } from 'react'
import { factories, IEmbedConfiguration, models, service } from 'powerbi-client'
import { Box } from '@mui/material'
import type { Report } from 'powerbi-client'
import Spinner from '../forms/Spinner'
import Unavailable from '../../pages/Unavailable'
import { useDeviceType } from '../../rsmCoreComponents/hooks/useDeviceType'
import api from '../../api'
import tokens from '../../styles/tokens.json'

const MINUTES_BEFORE_EXPIRATION = 10
const MIN_REFRESH_INTERVAL_MS = 60_000

interface ChildReportEmbed {
  reportId: string
  embedUrl: string
  accessToken: string
  tokenExpiration: string
  isDashboard: boolean
  childReportDisplayName: string
}

interface PowerBIChildReportProps {
  parentReportName: string | undefined
  childReportName: string | undefined
  onReportDisplayNameChange?: (name: string) => void
  onLoadedChange?: (loaded: boolean) => void
}

const defaultChildReportData: ChildReportEmbed = {
  reportId: '',
  embedUrl: '',
  accessToken: '',
  tokenExpiration: '',
  isDashboard: false,
  childReportDisplayName: '',
}

const PowerBIChildReport = ({
  parentReportName,
  childReportName,
  onReportDisplayNameChange,
  onLoadedChange,
}: PowerBIChildReportProps) => {
  const { isMobile, isTablet } = useDeviceType()
  const reportDiv = useRef<HTMLDivElement>(null)
  const embeddedReportRef = useRef<Report | null>(null)
  const refreshTimeoutRef = useRef<number | undefined>(undefined)
  const isRefreshingRef = useRef(false)

  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [tokenExpiration, setTokenExpiration] = useState<string>('')
  const [failedToRetrieveReport, setFailedToRetrieveReport] = useState(false)

  const powerbiRef = useRef(
    new service.Service(
      factories.hpmFactory,
      factories.wpmpFactory,
      factories.routerFactory,
    ),
  )

  async function loadReport(
    embedUrl: string,
    accessToken: string,
    reportId: string,
  ) {
    if (embedUrl === '' || accessToken === '' || reportId === '') return

    const reportContainer = reportDiv.current

    if (reportContainer) {
      powerbiRef.current.reset(reportContainer)
    }

    if (reportContainer) {
      const embedConfig: IEmbedConfiguration = {
        type: 'report',
        id: reportId,
        embedUrl,
        accessToken,
        tokenType: models.TokenType.Embed,
      }

      const report = (await powerbiRef.current.embed(
        reportContainer,
        embedConfig,
      )) as Report

      embeddedReportRef.current = report

      report.off('loaded')
      report.on('loaded', () => undefined)
    }
  }

  async function getChildReportData(): Promise<ChildReportEmbed> {
    if (!parentReportName || !childReportName) {
      setFailedToRetrieveReport(true)
      return defaultChildReportData
    }

    try {
      const response = await api.user.report_GetChildReportEmbed(
        parentReportName,
        childReportName,
        undefined,
      )

      const data = response?.data

      return {
        reportId: data?.reportId ?? '',
        embedUrl: data?.embedUrl ?? '',
        accessToken: data?.accessToken ?? '',
        tokenExpiration: data?.tokenExpiration ?? '',
        isDashboard: data?.isDashboard ?? false,
        childReportDisplayName: data?.reportDisplayName ?? '',
      }
    } catch {
      setFailedToRetrieveReport(true)
      return defaultChildReportData
    }
  }

  async function updateToken() {
    if (isRefreshingRef.current) return
    if (!parentReportName || !childReportName) return

    isRefreshingRef.current = true
    const updatedReport = await getChildReportData()
    isRefreshingRef.current = false

    if (
      updatedReport.reportId.length === 0 ||
      updatedReport.embedUrl.length === 0 ||
      updatedReport.accessToken.length === 0
    ) {
      setFailedToRetrieveReport(true)
      return
    }

    const reportContainer = reportDiv.current
    if (!reportContainer) return

    const report = powerbiRef.current.get(reportContainer) as Report
    const newExpiration = updatedReport.tokenExpiration || ''
    const parsedExpiration = Date.parse(newExpiration)

    if (!newExpiration || Number.isNaN(parsedExpiration)) {
      setTokenExpiration('')
      return
    }

    await report.setAccessToken(updatedReport.accessToken)

    if (newExpiration !== tokenExpiration) {
      setTokenExpiration(newExpiration)
    }
  }

  const getDeviceWidth = () => {
    if (typeof window !== 'undefined') {
      const mainContent = document.querySelector(
        '.MainContent',
      ) as HTMLElement | null

      if (mainContent) {
        const padding = isTablet ? 64 : 208
        return mainContent.clientWidth - padding
      }

      return window.innerWidth
    }

    return 0
  }

  const getReportHeight = () => (isMobile || isTablet ? '40rem' : '51.25rem')

  const getReportContainerStyle = () => {
    const style: React.CSSProperties = {}

    style.display = 'flex'
    style.backgroundColor = tokens.colors.backgroundGray
    style.borderRadius = '0.5rem'
    style.overflow = 'hidden'
    style.position = 'relative'
    style.maxWidth = getDeviceWidth()
    style.minWidth = isTablet ? 'calc(100vw - 4rem)' : 'calc(100vw - 13rem)'
    style.height = getReportHeight()
    style.width = isTablet ? 'calc(100vw - 4rem)' : '100%'

    return style
  }

  useEffect(() => {
    let isMounted = true
    const getReportInfo = async () => {
      if (!isMounted) return

      setIsLoaded(false)
      setFailedToRetrieveReport(false)
      setTokenExpiration('')
      onLoadedChange?.(false)

      const report = await getChildReportData()
      if (!isMounted) return
      onReportDisplayNameChange?.(report.childReportDisplayName)

      if (
        report.embedUrl.length === 0 ||
        report.accessToken.length === 0 ||
        report.reportId.length === 0
      ) {
        if (!isMounted) return
        setFailedToRetrieveReport(true)
        setIsLoaded(true)
        onLoadedChange?.(true)
        return
      }

      setTokenExpiration(report.tokenExpiration)
      await loadReport(report.embedUrl, report.accessToken, report.reportId)
      if (!isMounted) return
      setIsLoaded(true)
      onLoadedChange?.(true)
    }
    getReportInfo()
    return () => {
      isMounted = false
    }
  }, [
    parentReportName,
    childReportName,
    onLoadedChange,
    onReportDisplayNameChange,
  ])

  useEffect(() => {
    if (refreshTimeoutRef.current !== undefined) {
      clearTimeout(refreshTimeoutRef.current)
    }

    if (!tokenExpiration) return undefined

    const expirationTime = Date.parse(tokenExpiration)
    if (Number.isNaN(expirationTime)) return undefined

    const currentTime = Date.now()
    const bufferTime = MINUTES_BEFORE_EXPIRATION * 60 * 1000
    const rawDelay = expirationTime - currentTime - bufferTime
    const refreshDelay = Math.max(rawDelay, MIN_REFRESH_INTERVAL_MS)

    refreshTimeoutRef.current = window.setTimeout(() => {
      updateToken()
    }, refreshDelay)

    const visibilityHandler = () => {
      if (!document.hidden) {
        const now = Date.now()
        if (now >= expirationTime - bufferTime) {
          updateToken()
        }
      }
    }

    document.addEventListener('visibilitychange', visibilityHandler)

    return () => {
      if (refreshTimeoutRef.current !== undefined) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = undefined
      }

      document.removeEventListener('visibilitychange', visibilityHandler)
    }
  }, [tokenExpiration])

  return (
    <Box id="MainContent" sx={{ backgroundColor: tokens.colors.white }}>
      {failedToRetrieveReport && <Unavailable />}

      {!failedToRetrieveReport && (
        <>
          <Spinner open={!isLoaded} />
          <Box sx={getReportContainerStyle()} ref={reportDiv} />
        </>
      )}
    </Box>
  )
}

export default PowerBIChildReport
